/**
 * Cart Remove Confirmation Modal
 *
 * Intercepts clicks on .line-item__close-btn and shows a confirmation dialog
 * before proceeding with removal. Uses capture-phase so it runs before the
 * LineItemQuantity click handler in theme.js.
 */

(function () {
  const MODAL_ID = "cart-remove-confirm-modal";
  const CONFIRM_BTN_ID = "cart-remove-confirm-yes";
  const BODY_EL_ID = "cart-remove-confirm-body";

  /** @type {HTMLAnchorElement|null} */
  let pendingRemoveLink = null;

  /**
   * Flag set to true only during the programmatic re-dispatch after the user
   * confirms removal. Lets the capture listener pass the event through without
   * re-opening the modal.
   */
  let isConfirmedDispatch = false;

  const getModal = () => document.getElementById(MODAL_ID);

  const openModal = (removeLink) => {
    const modal = getModal();
    if (!modal) return;

    pendingRemoveLink = removeLink;

    const title = resolveProductTitle(removeLink);
    const bodyEl = document.getElementById(BODY_EL_ID);

    if (bodyEl) {
      const template = window.cartRemoveConfirmStrings?.body ?? "";
      const bodyText = template.replace("__TITLE__", title);

      bodyEl.innerHTML = "";
      bodyText.split("\n").forEach((line, index) => {
        if (index > 0) bodyEl.appendChild(document.createElement("br"));
        bodyEl.appendChild(document.createTextNode(line));
      });
    }

    modal.setAttribute("aria-hidden", "false");
    modal.removeAttribute("inert");
    document.documentElement.classList.add("lock");

    const firstFocusable = modal.querySelector("button");
    firstFocusable?.focus();
  };

  const closeModal = () => {
    const modal = getModal();
    if (!modal) return;

    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("inert", "");
    document.documentElement.classList.remove("lock");
    pendingRemoveLink = null;
  };

  const handleConfirm = () => {
    const link = pendingRemoveLink;
    closeModal();

    if (!link) return;

    isConfirmedDispatch = true;
    link.dispatchEvent(
      new MouseEvent("click", { bubbles: true, cancelable: true }),
    );
    isConfirmedDispatch = false;
  };

  const resolveProductTitle = (removeLink) => {
    const lineItem = removeLink.closest("line-item, .line-item, tr");
    if (!lineItem) return "";

    const titleEl = lineItem.querySelector(
      ".line-item__title, .line-item__name, [data-product-title]",
    );
    return titleEl ? titleEl.textContent.trim() : "";
  };

  /**
   * Capture-phase listener — runs before LineItemQuantity's bubble listener.
   */
  const handleDocumentClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // Close triggers: overlay backdrop, X button, cancel button
    if (target.closest("[data-cart-remove-confirm-close]")) {
      closeModal();
      return;
    }

    // Confirm button
    if (target.closest(`#${CONFIRM_BTN_ID}`)) {
      handleConfirm();
      return;
    }

    // Remove "X" link — intercept unless this is our own confirmed re-dispatch
    const removeBtn = target.closest(".line-item__close-btn");
    if (!removeBtn) return;
    if (isConfirmedDispatch) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openModal(removeBtn);
  };

  const handleKeyDown = (event) => {
    const modal = getModal();
    if (!modal || modal.getAttribute("aria-hidden") === "true") return;

    if (event.key === "Escape") {
      closeModal();
    }
  };

  const init = () => {
    // Set initial inert so the modal is not keyboard-navigable when hidden
    const modal = getModal();
    if (modal) modal.setAttribute("inert", "");

    document.addEventListener("click", handleDocumentClick, true);
    document.addEventListener("keydown", handleKeyDown);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
